import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'sonner';

import {
  ApplyToProgramDocument,
  GetViewerAvailableProgramsDocument,
  GetViewerProgramEnrollmentsWithCoverageDocument,
} from '@sandtech-hub-monorepo/ehub-client';

import {
  PlanType,
  enrolledEvent,
  enrolledParams,
  trackEvent,
} from '@sandtech-hub-monorepo/ui-auth-kit';

import { AppRoutesPaths } from '../routes/types';

interface EnrollOptions {
  /**
   * A live subscription already unlocks the program. The learner lands on the
   * program itself rather than /learning, and is told it is included rather
   * than that their free modules are open.
   */
  included?: boolean;
  /** Started on the free Preview tier: `plan_type: trial` in analytics. */
  trial?: boolean;
}

interface UseEnrollInProgramResult {
  /** Enrol, then land the learner on their learning page or the program. */
  enroll: (programId: string, options?: EnrollOptions) => void;
  enrolling: boolean;
}

/**
 * Enrolling is the last step of picking how to start a program, not the first.
 * Both ways through the plan picker end here: "Stay on Free" enrols straight
 * away, and a paid plan enrols once the checkout is confirmed.
 *
 * Despite the mutation's name there is no admissions review on this path —
 * `applyToProgram` writes a ProgramEnrollment with status ONGOING and returns.
 * Enrolment is free and separate from the subscription, which is what unlocks
 * the paid modules of whatever it covers.
 *
 * Several call sites, so it stays a hook rather than being inlined.
 */
export const useEnrollInProgram = (): UseEnrollInProgramResult => {
  const navigate = useNavigate();

  /*
   * Read cache-first, for `isEnrolled` only. Every route into this already
   * queries the catalogue, so the answer is warm and costs no request.
   */
  const { data } = useQuery(GetViewerAvailableProgramsDocument, {
    fetchPolicy: 'cache-first',
  });

  // Set by `enroll` for the call in flight: the mutation's own onCompleted
  // also carries the analytics, so it is not replaced per call.
  const optionsRef = useRef<EnrollOptions>({});

  const programPath = (programId: string) => {
    const abbreviation = data?.viewer?.availablePrograms?.find(
      (program) => program.id === programId
    )?.abbreviation;

    return abbreviation
      ? AppRoutesPaths.Program.replace(':programId', abbreviation)
      : AppRoutesPaths.Learning;
  };

  /*
   * Both queries are refetched because both change: `availablePrograms` carries
   * the isEnrolled/canEnroll flags the catalogue and detail page render, and
   * the enrollment list is what the learning page lists. Awaited, so the
   * program is already there when the learner arrives rather than appearing a
   * moment later.
   */
  const [applyToProgram, { loading: enrolling }] = useMutation(
    ApplyToProgramDocument,
    {
      // The server's refusals name the actual problem, so they are shown
      // rather than replaced with a house message.
      onError: (error) => toast.error(error.message),
      onCompleted: (result, options) => {
        const programId = options?.variables?.programInput.programId;
        const user = result.applyToProgram?.user;
        const program = user?.programEnrollments?.find(
          (pe) => pe.program?.id === programId
        )?.program;
        if (program) {
          trackEvent(
            enrolledEvent(user),
            enrolledParams(
              program,
              user,
              optionsRef.current.trial ? PlanType.Trial : PlanType.Paid
            )
          );
        }

        if (optionsRef.current.included && programId) {
          toast.success(
            'You are enrolled. This program is included in your subscription.'
          );
          navigate(programPath(programId));
          return;
        }

        toast.success('You are enrolled. Your free modules are open.');
        navigate(AppRoutesPaths.Learning);
      },
      refetchQueries: [
        GetViewerAvailableProgramsDocument,
        GetViewerProgramEnrollmentsWithCoverageDocument,
      ],
      awaitRefetchQueries: true,
    }
  );

  const enroll = (programId: string, options: EnrollOptions = {}) => {
    const { included = false } = options;
    /*
     * Already enrolled is not an error to show anyone. It is reachable
     * normally: the catalogue's Start button opens the picker whatever the
     * learner's state, and a second subscription bought for a program they are
     * already in comes back through here. `applyToProgram` would refuse it —
     * "Learner is already actively enrolled into this program" — so the
     * mutation is skipped and the learner just goes where they were going.
     */
    const alreadyEnrolled = (data?.viewer?.availablePrograms ?? []).some(
      (program) => program.id === programId && program.isEnrolled
    );

    if (alreadyEnrolled) {
      navigate(included ? programPath(programId) : AppRoutesPaths.Learning);
      return;
    }

    optionsRef.current = options;
    applyToProgram({ variables: { programInput: { programId } } });
  };

  return { enroll, enrolling };
};
